/**
 * The commander table at the top of box 4.
 *
 * The Incident Commander column is the roster's, so a row exists only while
 * someone holds that seat and the name cannot be typed over. Deputy and Agency
 * are typed per row and stored against derived position keys, `<seat> Deputy`
 * and `<seat> Agency`, which is what the PDF export reads them back by.
 */

import type React from 'react';
import { useState } from 'react';
import { Check, Pencil, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { RosterEntry } from 'components/IAP/hooks/incidentRoster/incidentRoster.types';

import { ICS204_TABLE } from '../AssignmentList/assignmentCardStyles';
import { BoxInfo } from '../icsBoxPrimitives';
import {
  ICS203_COMMANDER_COLUMNS,
  ICS203_ROSTER_FIELD_TOOLTIP,
} from '../ics203BoxLabels';
import {
  getRosterAssigneesDisplay,
  INCIDENT_COMMANDER_SEATS,
} from './ics203Roster';

export type Ics203CommanderTableProps = {
  rosterData: RosterEntry[] | undefined;
  assignments: Map<string, string>;
  onSave: (patch: { deputy: string; agency: string }, seat: string) => void;
  readOnly?: boolean;
};

export const Ics203CommanderTable: React.FC<Ics203CommanderTableProps> = ({
  rosterData,
  assignments,
  onSave,
  readOnly = false,
}) => {
  const [editingSeat, setEditingSeat] = useState<string | null>(null);
  const [draft, setDraft] = useState({ deputy: '', agency: '' });

  const rows = INCIDENT_COMMANDER_SEATS.filter(
    (seat) => getRosterAssigneesDisplay(rosterData, seat).isAssigned
  );

  const startEdit = (seat: string) => {
    setEditingSeat(seat);
    setDraft({
      deputy: assignments.get(`${seat} Deputy`) ?? '',
      agency: assignments.get(`${seat} Agency`) ?? '',
    });
  };

  if (rows.length === 0) {
    return (
      <p className="tw:text-sm tw:text-muted-foreground">
        No incident commanders assigned. Rows appear here once the Incident
        Roster puts someone in a commander seat.
      </p>
    );
  }

  return (
    <div className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2">
      <div className="tw:overflow-x-auto">
        {/* Commander names come from a roster join, so a seat held by several
            people prints as a long composite string. The shared cell defaults
            are a fixed height and no wrapping, which clips it, so this table
            lets its cells grow instead. */}
        <Table
          className={`${ICS204_TABLE} tw:[&_td]:h-auto tw:[&_td]:align-top tw:[&_td]:whitespace-normal`}
          data-testid="ics203-commanders"
        >
          <TableHeader>
            <TableRow>
              {ICS203_COMMANDER_COLUMNS.map((column) => (
                <TableHead key={column.label}>{column.label}</TableHead>
              ))}
              {readOnly ? null : <TableHead className="tw:w-24" />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((seat) => {
              const { display } = getRosterAssigneesDisplay(rosterData, seat);
              const isEditing = editingSeat === seat;

              return (
                <TableRow key={seat}>
                  <TableCell>
                    <div className="tw:flex tw:items-center tw:gap-2">
                      <span className="tw:min-w-0 tw:break-words">
                        {display}
                      </span>
                      <BoxInfo label={ICS203_ROSTER_FIELD_TOOLTIP}>
                        {ICS203_ROSTER_FIELD_TOOLTIP}
                      </BoxInfo>
                    </div>
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <Input
                        className="tw:min-w-32"
                        aria-label={`Deputy for ${display}`}
                        placeholder="Enter deputy..."
                        value={draft.deputy}
                        onChange={(event) =>
                          setDraft({ ...draft, deputy: event.target.value })
                        }
                      />
                    ) : (
                      assignments.get(`${seat} Deputy`) || '—'
                    )}
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <Input
                        className="tw:min-w-32"
                        aria-label={`Agency for ${display}`}
                        placeholder="Enter agency..."
                        value={draft.agency}
                        onChange={(event) =>
                          setDraft({ ...draft, agency: event.target.value })
                        }
                      />
                    ) : (
                      assignments.get(`${seat} Agency`) || '—'
                    )}
                  </TableCell>
                  {readOnly ? null : (
                    <TableCell>
                      <div className="tw:flex tw:justify-end tw:gap-1">
                        {isEditing ? (
                          <>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={`Cancel editing ${display}`}
                              onClick={() => setEditingSeat(null)}
                            >
                              <X className="tw:size-4" />
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              aria-label={`Save ${display}`}
                              onClick={() => {
                                onSave(draft, seat);
                                setEditingSeat(null);
                              }}
                            >
                              <Check className="tw:size-4" />
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={`Edit ${display}`}
                            onClick={() => startEdit(seat)}
                          >
                            <Pencil className="tw:size-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <span className="tw:text-sm tw:text-muted-foreground">
        {rows.length} Incident Commander{rows.length === 1 ? '' : 's'}
      </span>
    </div>
  );
};

export default Ics203CommanderTable;
